import {
  deliveryDateRuleText,
  formatDeliveryDate,
  resolveActiveDatePolicy,
  resolveDeliveryDateChoices,
} from '@service/giftStore/utils/giftDeliveryDate.util';

import {
  DeliveryDatePolicyForm,
  formToDatePolicy,
  validateDatePolicyForm,
} from '../utils/giftDeliveryDatePolicyForm';

const input = 'w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm';
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 브랜드 폼 — 배송 희망일 지정 정책 편집 (deliveryDetail.datePolicy) */
export function GiftDeliveryDatePolicyEditor({
  value,
  onChange,
}: {
  value: DeliveryDatePolicyForm;
  onChange: (value: DeliveryDatePolicyForm) => void;
}) {
  const error = validateDatePolicyForm(value);
  // 구매자 화면에 나갈 안내를 지금 시각 기준으로 미리 보여 준다
  const preview = !error ? resolveActiveDatePolicy(formToDatePolicy(value)) : null;
  const earliest = preview
    ? resolveDeliveryDateChoices(preview, new Date()).earliestSelectable
    : null;

  return (
    <div className="space-y-3 rounded-lg border border-amber-100 bg-amber-50 p-4">
      <label className="flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        배송 희망일 지정 사용
      </label>
      <p className="text-xs text-gray-600">
        사용하면 주문서에서 구매자가 받을 날짜를 고릅니다. 한 배송지로 받는 일괄배송
        주문에만 적용됩니다.
      </p>
      {value.enabled && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              결제 마감시각
              <input
                className={input}
                type="time"
                value={value.cutoffTime}
                onChange={(e) => onChange({ ...value, cutoffTime: e.target.value })}
              />
            </label>
            <label className="text-sm">
              선택 가능 기간 (오늘부터 N일)
              <input
                className={input}
                type="number"
                min={0}
                step={1}
                value={value.maxDaysAhead}
                onChange={(e) => onChange({ ...value, maxDaysAhead: e.target.value })}
              />
            </label>
            <label className="text-sm">
              마감 전 결제 — 가장 빠른 수령일 (오늘 + N일)
              <input
                className={input}
                type="number"
                min={0}
                step={1}
                value={value.leadDaysBeforeCutoff}
                onChange={(e) =>
                  onChange({ ...value, leadDaysBeforeCutoff: e.target.value })
                }
              />
            </label>
            <label className="text-sm">
              마감 후 결제 — 가장 빠른 수령일 (오늘 + N일)
              <input
                className={input}
                type="number"
                min={0}
                step={1}
                value={value.leadDaysAfterCutoff}
                onChange={(e) =>
                  onChange({ ...value, leadDaysAfterCutoff: e.target.value })
                }
              />
            </label>
          </div>
          <p className="text-xs text-gray-600">
            1 = 내일, 2 = 모레(달력 일수 — 휴무일도 하루로 셉니다). 기준은 결제(입금) 완료
            시각이며 시각·날짜는 한국시간입니다.
          </p>

          <div className="text-sm">
            <p>
              배송 불가 요일{' '}
              <span className="text-xs text-gray-500">(눌러서 선택 · 여러 개 가능)</span>
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {WEEKDAYS.map((label, weekday) => {
                const selected = value.closedWeekdays.includes(weekday);
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={selected}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      selected
                        ? 'border-amber-600 bg-amber-600 text-white'
                        : 'border-gray-300 bg-white text-gray-700'
                    }`}
                    onClick={() =>
                      onChange({
                        ...value,
                        closedWeekdays: selected
                          ? value.closedWeekdays.filter((d) => d !== weekday)
                          : [...value.closedWeekdays, weekday],
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block text-sm">
            배송 불가일{' '}
            <span className="text-xs text-gray-500">
              (명절·휴무 — 한 줄에 하나, 예: 2026-10-05)
            </span>
            <textarea
              className={`${input} h-20`}
              value={value.closedDates}
              onChange={(e) => onChange({ ...value, closedDates: e.target.value })}
              placeholder={'2026-10-05\n2026-10-06'}
            />
          </label>

          <label className="block text-sm">
            도착 시간 강조 문구{' '}
            <span className="text-xs text-gray-500">
              (주문서·상품 상세에 크게 표시, 비우면 미표시)
            </span>
            <input
              className={input}
              type="text"
              maxLength={30}
              value={value.arrivalNotice}
              onChange={(e) => onChange({ ...value, arrivalNotice: e.target.value })}
              placeholder="예: 오전 중 배송됩니다"
            />
          </label>

          {error ? (
            <p className="text-xs font-semibold text-red-600">{error}</p>
          ) : (
            preview && (
              <div className="rounded border border-amber-200 bg-white p-3 text-xs text-gray-700">
                <p className="font-semibold text-gray-500">구매자 안내 미리보기</p>
                {preview.arrivalNotice && (
                  <p className="mt-1 text-sm font-bold">{preview.arrivalNotice}</p>
                )}
                <p className="mt-1">{deliveryDateRuleText(preview)}</p>
                <p className="mt-0.5">
                  지금 결제 시 가장 빠른 수령일:{' '}
                  <b>
                    {earliest ? formatDeliveryDate(earliest) : '선택 가능한 날짜 없음'}
                  </b>
                </p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
